// The step-up dialog (ADR-0019): when the api answers `step_up_required`, the person confirms it is them with a
// fresh code, or their PIN where the action allows one, and the action they started runs again by itself. Each
// step-up is for one action: the web app remembers none, and closing the dialog leaves everything as it was.
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Dialog } from "../Dialog.js";
import { ApiError } from "../http.js";
import { useWords, type Words } from "../i18n/words.js";
import { stepUpFor, type SensitiveAction, type StepUpApi } from "./api.js";

/** What is about to happen, by the action, when the page that started it says nothing more exact. */
const WHAT = {
  "bank_details.change": "stepUpBankDetails",
  "roles.change": "stepUpRoles",
  "owner.add": "stepUpOwner",
  "export.full": "stepUpExport",
  "payment.approve": "stepUpPayment",
  "pin.change": "stepUpPinChange",
  "tenant.offboard": "stepUpOffboard",
} as const satisfies Record<SensitiveAction, keyof Words>;

/** Why asking for a code did not work, in plain words. */
function codeRequestProblem(error: unknown, w: Words): string {
  if (!(error instanceof ApiError)) return w.ownerPagesUnreachable;
  if (error.status === 401) return w.stepUpSignedOut;
  if (error.status === 409) return w.stepUpNoMethod;
  if (error.status === 429)
    return error.code === "locked" || error.code === "blocked" ? w.stepUpTooManyTries : w.stepUpTooManyCodes;
  return error.problems.length > 0 ? error.problems.join("; ") : w.ownerPagesRefused(error.code);
}

/** Why a code or PIN did not step up, in plain words. */
function confirmProblem(error: unknown, w: Words): string {
  if (!(error instanceof ApiError)) return w.ownerPagesUnreachable;
  if (error.code === "invalid_code") return w.stepUpWrongCode;
  if (error.code === "invalid_pin") return w.stepUpWrongPin;
  if (error.code === "code_required") return w.stepUpCodeRequired;
  if (error.status === 401) return w.stepUpSignedOut;
  if (error.status === 429) return w.stepUpTooManyTries;
  return error.problems.length > 0 ? error.problems.join("; ") : w.ownerPagesRefused(error.code);
}

export function StepUpDialog({
  api,
  action,
  pinAllowed,
  what,
  onConfirmed,
  onClose,
}: {
  api: StepUpApi;
  action: SensitiveAction;
  /** Whether a PIN may confirm it, as the api said when it asked for the step-up. */
  pinAllowed: boolean;
  /** What is about to happen, in plain words; the action's own words when not given. */
  what?: string;
  onConfirmed: () => void;
  onClose: () => void;
}) {
  const w = useWords();
  const id = useId();
  // How many codes were sent: 0 until the first, then the Code field is shown.
  const [codesSent, setCodesSent] = useState(0);
  const sent = codesSent > 0;
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const codeInput = useRef<HTMLInputElement>(null);

  const sendCode = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      await api.requestCode(action);
      setCodesSent((n) => n + 1);
      setCode("");
    } catch (error) {
      setProblem(codeRequestProblem(error, w));
    } finally {
      setBusy(false);
    }
  };

  // After each code, move to the field so the next key types it. An effect runs once React has drawn the field
  // (sendCode clears busy in the same render); a frame asked for in sendCode could come before that.
  useEffect(() => {
    if (codesSent > 0) codeInput.current?.focus();
  }, [codesSent]);

  const confirm = async (event: FormEvent, proof: { code: string } | { pin: string }) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await api.confirm(action, proof);
      onConfirmed();
    } catch (error) {
      setProblem(confirmProblem(error, w));
      setBusy(false);
    }
  };

  return (
    <Dialog title={w.stepUpTitle} onClose={onClose} small describedBy={`${id}-what`}>
      <div className="step-up">
        <p id={`${id}-what`}>
          <strong>{what ?? w[WHAT[action]]}</strong> {w.stepUpWhy}
        </p>
        {sent ? (
          <form onSubmit={(e) => void confirm(e, { code: code.trim() })}>
            <p role="status">{w.stepUpSent}</p>
            <label className="field" htmlFor={`${id}-code`}>
              {w.stepUpCode}
            </label>
            <input
              ref={codeInput}
              id={`${id}-code`}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={16}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <div className="step-up-actions">
              <button type="submit" className="btn btn-go" disabled={busy || code.trim() === ""}>
                {w.stepUpConfirm}
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => void sendCode()}>
                {w.stepUpSendAgain}
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="btn btn-go" disabled={busy} onClick={() => void sendCode()}>
            {w.stepUpSendCode}
          </button>
        )}
        {pinAllowed ? (
          <form onSubmit={(e) => void confirm(e, { pin })}>
            <label className="field" htmlFor={`${id}-pin`}>
              {w.stepUpOrPin}
            </label>
            <input
              id={`${id}-pin`}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={16}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
            <button type="submit" className="btn" disabled={busy || pin === ""}>
              {w.stepUpConfirmPin}
            </button>
          </form>
        ) : null}
        {problem ? (
          <p className="alert" role="alert">
            {problem}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/** A refusal for an action whose step-up the person closed: nothing was done. */
export const stepUpClosed = (w: Words) => new ApiError(403, "step_up_closed", [w.stepUpClosed]);

interface Asking {
  readonly key: number;
  readonly action: SensitiveAction;
  readonly pinAllowed: boolean;
  readonly what: string | undefined;
  readonly confirmed: () => void;
  readonly closed: () => void;
}

/**
 * Runs an action that may need a step-up: when the api asks for one, the dialog opens, and once the person has
 * confirmed it is them the action runs again. Closing the dialog rejects with `stepUpClosed`, whose problem says
 * nothing was done. Every step-up asked for settles: one asked for while another is open closes the open one
 * first, and leaving the page closes the one still open. Render `dialog` on the page.
 */
export function useStepUp(api: StepUpApi): {
  run: <T>(work: () => Promise<T>, what?: string) => Promise<T>;
  dialog: ReactNode;
} {
  const w = useWords();
  const [asking, setAsking] = useState<Asking | undefined>(undefined);
  // The step-up still waiting to settle, read when the next is asked for and when the page goes.
  const open = useRef<Asking | undefined>(undefined);
  const asked = useRef(0);

  /** Settles the open step-up, if it is `which`, one way or the other; the dialog closes. */
  const settle = useCallback((which: Asking | undefined, how: "confirmed" | "closed") => {
    if (!which || open.current !== which) return;
    open.current = undefined;
    setAsking(undefined);
    which[how]();
  }, []);

  useEffect(() => () => settle(open.current, "closed"), [settle]);

  const run = useCallback(
    async <T,>(work: () => Promise<T>, what?: string): Promise<T> => {
      for (;;) {
        try {
          return await work();
        } catch (error) {
          const step = stepUpFor(error);
          if (!step) throw error;
          await new Promise<void>((confirmed, refused) => {
            settle(open.current, "closed");
            const next = { ...step, key: ++asked.current, what, confirmed, closed: () => refused(stepUpClosed(w)) };
            open.current = next;
            setAsking(next);
          });
        }
      }
    },
    [w, settle],
  );

  const dialog = asking ? (
    <StepUpDialog
      key={asking.key}
      api={api}
      action={asking.action}
      pinAllowed={asking.pinAllowed}
      {...(asking.what === undefined ? {} : { what: asking.what })}
      onConfirmed={() => settle(asking, "confirmed")}
      onClose={() => settle(asking, "closed")}
    />
  ) : null;

  return { run, dialog };
}
